import { addDays, startOfWeek, format, parseISO, subWeeks } from 'date-fns';

type MonthDaysData = {
  month: string;
  days: string[];
};

/**
 * Returns dates for the months excluding weekeends.
 */
export const getPast40WeeksDatesGroupedByMonths = (): Array<MonthDaysData> => {
  const monthsDates = getPast40WeeksStartDateGroupedByMonths();
  const results: MonthDaysData[] = [];

  monthsDates.forEach((monthsDate) => {
    const days: string[] = [];

    const { weekStartDates, month } = monthsDate;

    weekStartDates.forEach((weekStartDate) => {
      for (let i = 0; i < 5; i++) {
        const date = addDays(weekStartDate, i);
        days.push(format(date, 'yyyy-MM-dd'));
      }
    });

    results.push({
      month,
      days,
    });
  });

  return results;
};

/**
 * Returns start date for the first week and endDate for the last week
 */
export const getPast40WeeksStartAndEndDate = (): {
  firstWeekStartDate: string;
  lastWeekEndDate: string;
} => {
  const monthsData = getPast40WeeksDatesGroupedByMonths();
  const firstMonthDays = monthsData[0].days;
  const lastMonthDays = monthsData[monthsData.length - 1].days;

  const firstWeekStartDate = firstMonthDays[0];
  const lastWeekEndDate = lastMonthDays[lastMonthDays.length - 1];

  return { firstWeekStartDate, lastWeekEndDate };
};

/**
 * Helper function to get start date of week for past 40 weeks
 * Week Starts on Monday
 */
const getPast40WeeksStartDates = (): Date[] => {
  const currentDate: Date = new Date(); // Current date
  const dates: Date[] = [];

  // Generate dates for each week starting from 39 weeks ago to the current week (0 weeks ago)
  for (let i: number = 39; i >= 0; i--) {
    // Calculate the start of the week for the current week (subtracting i weeks from the current date)
    const startOfCurrentWeek: Date = startOfWeek(subWeeks(currentDate, i), { weekStartsOn: 1 });

    // Push the start of the week date into the dates array
    dates.push(startOfCurrentWeek);
  }

  return dates;
};

type MonthWeekData = {
  index: number;
  month: string;
  weekStartDates: Date[];
};

/**
 * Helper function to group week start dates into month, if week end date fall
 * on next month, the week is moved to next month.
 */
const getPast40WeeksStartDateGroupedByMonths = () => {
  const last40WeeksDates = getPast40WeeksStartDates();
  const results: Record<string, MonthWeekData> = {};

  let monthIndex = 0;

  last40WeeksDates.forEach((weekStartDate: Date) => {
    const weekEndDate = addDays(weekStartDate, 5);
    const monthName = format(weekEndDate, 'MMM');

    if (!results[monthName]) {
      results[monthName] = {
        index: monthIndex,
        month: monthName,
        weekStartDates: [weekStartDate],
      };
      monthIndex++;
      return;
    }

    const currentMonthData = results[monthName];
    results[monthName] = {
      ...currentMonthData,
      weekStartDates: [...currentMonthData.weekStartDates, weekStartDate],
    };
  });

  // Sort by oldest month
  const dataArray = Object.keys(results).map((monthName) => results[monthName]);
  const sortedByOldestMonth = dataArray.sort((a, b) => a.index - b.index);

  return sortedByOldestMonth;
};

export const getFormattedDateForTooltip = (date: string) => {
  const dateObj = parseISO(date);

  // Format the date to 'MMM d' (e.g., 'Feb 13')
  const formattedDate = format(dateObj, 'MMM d');

  return formattedDate;
};
